import classnames from 'classnames'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'

// Utils
import { ROW_SELECTOR_PLACEHOLDERS } from '../filterSection/constants/constants'
import Icon from '../icon'

import ChevronDown from './icons/ChevronDown.inline.svg'
import Cross from './icons/Cross.inline.svg'
import EmeraldRing from './icons/EmeraldRing.inline.svg'
import OvalRing from './icons/OvalRing.inline.svg'
import RoundRing from './icons/RoundRing.inline.svg'
import styles from './select.module.scss'

type StoneIconMap = Record<string, any>
const STONE_ICON_MAP: StoneIconMap = {
  Oval: OvalRing,
  Round: RoundRing,
  Emerald: EmeraldRing,
}

export interface SelectOptionProps {
  label: ReactNode
  value: string
  hex?: string
  isLink?: boolean
  linkPath?: string
  imgURL?: string
}

export interface SelectProps {
  id: string
  placeholder: string
  options: SelectOptionProps[]
  className?: string
  showMultipleColumns?: boolean
  selectedOptions?: string[]
  OnOptionClick: (e: React.MouseEvent<HTMLDivElement>, optionValue: string) => void
  bodyClassName?: string
  hiddenOptionContainer?: boolean
  showSelectedOptionOnLabel?: boolean
  showSelectedOptionOnDropDown?: boolean
  sendSortOpenEvent?: () => void
  isSortOption?: boolean
}

const OPTION_WRAPPER_ELEMENT_TYPE = 'OptionWrapper'

const Select = ({
  id,
  placeholder,
  options = [],
  className,
  OnOptionClick,
  showMultipleColumns = false,
  selectedOptions = [],
  bodyClassName,
  hiddenOptionContainer,
  showSelectedOptionOnLabel = false,
  showSelectedOptionOnDropDown = false,
  sendSortOpenEvent,
  isSortOption,
}: SelectProps) => {
  const isMultiSelect = ROW_SELECTOR_PLACEHOLDERS.includes(placeholder.toUpperCase()) && options.length > 1
  const [isListOpen, setIsListOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState('')
  const classes = classnames(styles.wrapper, className)
  const openList = useCallback(() => {
    setIsListOpen(lastState => !lastState)
    if (sendSortOpenEvent) {
      sendSortOpenEvent()
    }
  }, [])

  useEffect(() => {
    if (!isMultiSelect && selectedOptions && selectedOptions[0] !== selectedValue) {
      setSelectedValue(selectedOptions ? selectedOptions[0] : '')
    }
  }, [selectedOptions, isMultiSelect, selectedValue])

  const handleDocClick = useCallback(
    (e: any) => {
      const element = e.target?.closest(`#${id}`)
      const fromSelectNode = element !== null
      const clickTargetWasInsideOptionBody =
        fromSelectNode && e.target?.closest(`[data-element-type="${OPTION_WRAPPER_ELEMENT_TYPE}"]`) !== null
      if (!fromSelectNode || (clickTargetWasInsideOptionBody && !isMultiSelect)) {
        setIsListOpen(false)
      }
    },
    [isMultiSelect, id],
  )

  useEffect(() => {
    document.addEventListener('click', handleDocClick)
    return () => {
      document.removeEventListener('click', handleDocClick)
    }
  }, [handleDocClick])

  const isColorOption = placeholder.toUpperCase() === 'COLOR'
  const isStoneShape = placeholder.toUpperCase() === 'STONE SHAPE'

  return (
    <div id={id} className={classes}>
      <div className={styles.wrapper__header} onClick={openList}>
        <div className={styles.wrapper__header__text}>
          {placeholder}
          {showSelectedOptionOnLabel && !isMultiSelect && (
            <span className={styles.desktopOnly}>{selectedValue ? ':' : null}</span>
          )}
        </div>
        {showSelectedOptionOnLabel && !isMultiSelect && (
          <div
            className={classnames(styles.wrapper__header__text, styles.activeItem, styles.wrapper__body__item__value)}
          >
            {options.find(option => option.value === selectedValue)?.label}
          </div>
        )}
        <div>
          <Icon
            iconSvg={ChevronDown}
            className={classnames(styles.chevronIcon, isListOpen ? styles.wrapper__header__sign__rotate : '')}
          />
        </div>
      </div>
      {(hiddenOptionContainer || isListOpen) && (
        <div
          data-element-type={OPTION_WRAPPER_ELEMENT_TYPE}
          data-is-multi={isMultiSelect}
          data-filter-name={id}
          className={classnames(
            styles.wrapper__body,
            showMultipleColumns && styles.wrapper__showMultipleColumns,
            bodyClassName,
            isListOpen ? '' : styles.hide,
            isSortOption && styles.wrapper__sortOptions,
          )}
        >
          {options.map(option => {
            const isOptionSelected = isMultiSelect
              ? selectedOptions?.includes(option.value)
              : selectedValue === option.value
            return (
              <div
                key={option.value}
                data-value={option.value}
                data-is-link={option.isLink}
                data-is-selected={isOptionSelected}
                className={classnames(
                  styles.wrapper__body__item,
                  isStoneShape && styles.stoneShapeWrapper,
                  showSelectedOptionOnDropDown && !isMultiSelect && isOptionSelected && styles.bold,
                )}
                onClick={e => {
                  if (OnOptionClick) {
                    OnOptionClick(e, option.value)
                    if (!isMultiSelect) {
                      setSelectedValue(option.value)
                    }
                  }
                }}
              >
                {isMultiSelect && (
                  <div
                    className={classnames(
                      styles.checkbox,
                      selectedOptions?.includes(option.value) && styles.border,
                      isColorOption && option.imgURL && styles.imgWrapper,
                    )}
                  >
                    {isOptionSelected && placeholder.toUpperCase() !== 'COLOR' && <Cross className={styles.cross} />}
                    {isColorOption && (
                      <div
                        className={classnames(styles.checkbox__color)}
                        style={
                          option.imgURL
                            ? {
                                backgroundImage: `url(${option.imgURL})`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                              }
                            : { background: option.hex }
                        }
                        role="presentation"
                      />
                    )}
                  </div>
                )}
                {option.isLink ? (
                  <a
                    href={option.linkPath || `/${option.value}`}
                    onClick={e => {
                      e.preventDefault()
                    }}
                  >
                    {option.label}
                  </a>
                ) : (
                  option.label
                )}
                {isStoneShape && <Icon className={styles.stoneShapeIcon} iconSvg={STONE_ICON_MAP[option.value]} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Select
